/**
 * `<HomeSections>` — renders a resolved composition in order (spec 42 R7).
 *
 * The single place that maps `type → component`. Two rules live here rather
 * than in each section:
 *
 *  - **An unknown `type` is skipped, never fatal** (R1/AC21). The server's
 *    `parseSections` already drops them, so this is the second line of defence
 *    for a payload rendered by an older build.
 *  - Sections carry their own resolved data, so this component takes no
 *    catalog query of its own.
 *
 * Consumed by both `apps/store` and the ERP composer preview — which is why it
 * lives in `@flint/ui` and takes its card renderer as a prop instead of
 * importing one (`apps/web` may not import `apps/store`; AGENTS §4).
 */
import type { CSSProperties, ReactNode } from "react";
import type { ResolvedSection } from "@flint/api-contract/storefront-catalog-types";
import { HeroSlider } from "./hero-slider.js";
import { StatsStrip, type StoreProfile } from "./stats-strip.js";
import { AboutBlock } from "./about-block.js";
import { AnnouncementBar } from "./announcement-bar.js";
import { CategoryGrid } from "./category-grid.js";
import { PromoBanner } from "./promo-banner.js";
import { RichText } from "./rich-text.js";
import { ImageText } from "./image-text.js";
import { Testimonials } from "./testimonials.js";
import { Faq } from "./faq.js";
import { LogoWall } from "./logo-wall.js";
import { Gallery } from "./gallery.js";
import { FeaturedProduct } from "./featured-product.js";
import { Contact } from "./contact.js";
import { Newsletter, LeadCapture } from "./enquiry.js";
import {
  ProductGridSection,
  CollectionRailSection,
  type SectionListing,
  type SectionRail,
} from "./product-grid.js";
import type { TargetContext } from "./resolve-target.js";

export interface HomeSectionsProps<T extends SectionListing> {
  sections: ResolvedSection[];
  storeName: string;
  profile: StoreProfile & { about: string | null };
  ctx: TargetContext;
  renderCard: (listing: T) => ReactNode;
  /** Wraps every section in the app's container/rhythm. */
  wrap?: (node: ReactNode, key: string, fullBleed: boolean) => ReactNode;
  phaseLabel?: (rail: SectionRail<T>) => string | undefined;
}

/** Sections that span the viewport rather than sitting inside the container. */
const FULL_BLEED = new Set(["hero_slider", "announcement_bar"]);

/**
 * Sections that take no background scheme because they invert the tokens
 * themselves.
 *
 * Distinct from `FULL_BLEED`, and deliberately a second set: hero and
 * announcement skip bands *because they span the viewport*, so they are also
 * laid out differently. The promo banner is an ordinary card inside the
 * container — only its colours are special — so folding it into `FULL_BLEED`
 * would skip the band **and** silently change its layout.
 *
 * Why it has to skip: `promo-banner.tsx` paints its card `var(--ink)` and its
 * text `var(--paper)` — the tokens used *backwards*, which is what makes it a
 * dark statement card. A band remaps those two for normal-polarity content
 * (`--ink` → light, `--paper` → translucent), which lands exactly wrong here:
 * the card takes the light value and the text becomes near-invisible on it.
 * The band is skipped rather than the banner re-coloured because a coloured
 * band behind a maximum-contrast card is not a look worth supporting.
 */
const SELF_STYLED = new Set(["promo_banner"]);

/**
 * Colour-scheme bands (module 22 D4). Each scheme paints a full-width band and
 * *remaps the theme tokens* for the content on top, so the section's own
 * `var(--ink)` / `var(--muted)` / `var(--line)` stay legible without any
 * per-section colour code — the same trick the theme itself uses.
 *
 * Two layers, on purpose: the outer band reads the *real* tokens for its
 * background (`bg`); the inner layer applies the overrides (`vars`) for the
 * content. Doing both on one element would make `background: var(--ink)` read
 * the just-overridden `--ink` and paint the band the text colour instead.
 */
const SCHEME: Record<"muted" | "accent" | "dark", { bg: string; vars: CSSProperties }> = {
  muted: { bg: "var(--paper)", vars: {} },
  accent: {
    bg: "var(--accent)",
    vars: {
      "--ink": "var(--accent-fg)",
      "--ink-2": "var(--accent-fg)",
      "--muted": "var(--accent-fg)",
      "--line": "rgba(255, 255, 255, 0.28)",
      "--paper": "rgba(255, 255, 255, 0.12)",
    } as CSSProperties,
  },
  dark: {
    bg: "var(--ink)",
    vars: {
      "--ink": "#f5f5f4",
      "--ink-2": "#f5f5f4",
      "--muted": "rgba(255, 255, 255, 0.72)",
      "--line": "rgba(255, 255, 255, 0.16)",
      "--paper": "rgba(255, 255, 255, 0.08)",
    } as CSSProperties,
  },
};

/** Inline twin of the storefront `<Container>` — HomeSections owns the band, so
 *  it re-centres the content itself rather than relying on the consumer's wrap. */
const BAND_CONTAINER: CSSProperties = {
  width: "100%",
  maxWidth: "var(--s-container, 72rem)",
  marginInline: "auto",
  paddingInline: "1.5rem",
};

export function HomeSections<T extends SectionListing>({
  sections,
  storeName,
  profile,
  ctx,
  renderCard,
  wrap,
  phaseLabel,
}: HomeSectionsProps<T>) {
  const nodes: ReactNode[] = [];

  // R10: an announcement bar always renders first in the body, regardless of
  // where it sits in the list. A "banner" three sections down is not a banner,
  // so its position is ours to decide, not the merchant's.
  const ordered = [
    ...sections.filter((s) => s.type === "announcement_bar"),
    ...sections.filter((s) => s.type !== "announcement_bar"),
  ];

  for (const section of ordered) {
    let node: ReactNode = null;

    switch (section.type) {
      case "hero_slider":
        node = (
          <HeroSlider slides={section.slides} autoplayMs={section.autoplayMs} ctx={ctx} />
        );
        break;
      case "stats_strip":
        node = <StatsStrip profile={profile} heading={section.heading} items={section.items} />;
        break;
      case "about_block":
        node = (
          <AboutBlock
            storeName={storeName}
            about={profile.about}
            heading={section.heading}
            body={section.body}
          />
        );
        break;
      case "collection_rail":
        node = (
          <CollectionRailSection
            rails={section.rails as unknown as SectionRail<T>[]}
            ctx={ctx}
            renderCard={renderCard}
            {...(phaseLabel ? { phaseLabel } : {})}
          />
        );
        break;
      case "product_grid":
        node = (
          <ProductGridSection
            heading={section.heading}
            subheading={section.subheading}
            listings={section.listings as unknown as T[]}
            footerLink={section.footerLink}
            ctx={ctx}
            renderCard={renderCard}
          />
        );
        break;
      case "announcement_bar":
        node = (
          <AnnouncementBar
            text={section.text}
            tone={section.tone}
            scheme={section.scheme}
            dismissible={section.dismissible}
            target={section.target}
            ctx={ctx}
          />
        );
        break;
      case "category_grid":
        node = (
          <CategoryGrid
            heading={section.heading}
            subheading={section.subheading}
            tiles={section.tiles as never}
            style={section.style}
            showCounts={section.showCounts}
            ctx={ctx}
          />
        );
        break;
      case "promo_banner":
        node = (
          <PromoBanner
            imageUrl={section.imageUrl}
            eyebrow={section.eyebrow}
            headline={section.headline}
            body={section.body}
            bullets={section.bullets}
            cta={section.cta}
            imageSide={section.imageSide}
            ctx={ctx}
          />
        );
        break;
      case "rich_text":
        node = <RichText heading={section.heading} body={section.body} align={section.align} />;
        break;
      case "image_text":
        node = (
          <ImageText
            imageUrl={section.imageUrl}
            heading={section.heading}
            body={section.body}
            imageSide={section.imageSide}
            cta={section.cta}
            ctx={ctx}
          />
        );
        break;
      case "testimonials":
        node = <Testimonials heading={section.heading} items={section.items} />;
        break;
      case "faq":
        node = <Faq heading={section.heading} items={section.items} />;
        break;
      case "logo_wall":
        node = <LogoWall heading={section.heading} items={section.items} grayscale={section.grayscale} />;
        break;
      case "gallery":
        node = <Gallery heading={section.heading} images={section.images} columns={section.columns} />;
        break;
      case "featured_product":
        node = (
          <FeaturedProduct
            eyebrow={section.eyebrow}
            headline={section.headline}
            body={section.body}
            priceLabel={section.priceLabel}
            imageUrl={section.imageUrl}
            cta={section.cta}
            ctx={ctx}
          />
        );
        break;
      case "contact":
        node = (
          <Contact
            heading={section.heading}
            body={section.body}
            address={section.address}
            showWhatsapp={section.showWhatsapp}
            ctx={ctx}
          />
        );
        break;
      case "newsletter":
        node = (
          <Newsletter
            heading={section.heading}
            subtext={section.subtext}
            buttonLabel={section.buttonLabel}
            ctx={ctx}
          />
        );
        break;
      case "lead_capture":
        node = (
          <LeadCapture
            heading={section.heading}
            subtext={section.subtext}
            buttonLabel={section.buttonLabel}
            ctx={ctx}
          />
        );
        break;
      default:
        // A type this build does not know — skip it, never throw (R1).
        node = null;
    }

    if (!node) continue;
    const key = section.id;

    // A colour scheme turns a normal section into a full-width band: the band
    // owns the vertical rhythm + inner container, and the section renders on
    // top with the remapped tokens. Full-bleed sections (hero, announcement)
    // own their look, so a scheme on them is ignored.
    const scheme = section.scheme;
    let fullBleed = FULL_BLEED.has(section.type);
    // `SELF_STYLED` skips the band without touching `fullBleed` — the section
    // still lays out inside the container, it just keeps its own colours.
    if (scheme && scheme !== "default" && !fullBleed && !SELF_STYLED.has(section.type)) {
      node = (
        <div style={{ backgroundColor: SCHEME[scheme].bg, paddingBlock: "var(--s-section-gap, 2rem)" }}>
          <div style={{ ...SCHEME[scheme].vars, ...BAND_CONTAINER }}>{node}</div>
        </div>
      );
      fullBleed = true;
    }

    // The same test id on both surfaces is what lets a test prove the composer
    // preview mounts the real component rather than a look-alike (AC25).
    const tagged = <div data-testid={`section-${section.type}`}>{node}</div>;
    nodes.push(wrap ? wrap(tagged, key, fullBleed) : <div key={key}>{tagged}</div>);
  }

  return <>{nodes}</>;
}
